import React, { useEffect, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Search, ChevronRight, Book } from 'lucide-react';
import { useHelpSearch } from '../../hooks/useHelpSearch';
import { useModalA11y } from '../../hooks/use-modal-a11y';
import { HelpArticle } from '../../types/help';

interface CommandPaletteProps {
  isOpen: boolean;
  onClose: () => void;
  onArticleSelect: (article: HelpArticle) => void;
}

export const CommandPalette: React.FC<CommandPaletteProps> = ({
  isOpen,
  onClose,
  onArticleSelect,
}) => {
  const { searchQuery, setSearchQuery, searchResults, clearSearch } = useHelpSearch();
  const [selectedIndex, setSelectedIndex] = useState(0);

  // Reset state when opened
  useEffect(() => {
    if (isOpen) {
      clearSearch();
      setSelectedIndex(0);
    }
  }, [isOpen, clearSearch]);

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex((prev) =>
            Math.min(prev + 1, searchResults.length - 1)
          );
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex((prev) => Math.max(prev - 1, 0));
          break;
        case 'Enter':
          e.preventDefault();
          if (searchResults[selectedIndex]) {
            handleSelect(searchResults[selectedIndex].article);
          }
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, searchResults, selectedIndex, onClose]);

  // Reset selected index when search results change
  useEffect(() => {
    setSelectedIndex(0);
  }, [searchResults]);

  const handleSelect = useCallback(
    (article: HelpArticle) => {
      onArticleSelect(article);
      onClose();
    },
    [onArticleSelect, onClose]
  );

  const { dialogRef } = useModalA11y<HTMLDivElement>(isOpen, onClose);

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        className="fixed inset-0 bg-black/50 z-50"
      />

      {/* Command Palette */}
      <div className="fixed inset-0 z-50 flex items-start justify-center pt-[20vh]">
        <motion.div
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-label="Help search"
          tabIndex={-1}
          initial={{ opacity: 0, scale: 0.95, y: -20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: -20 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className="w-full max-w-2xl bg-white dark:bg-secondary-800 rounded-xl shadow-2xl overflow-hidden focus:outline-none"
        >
          {/* Search input */}
          <div className="border-b border-gray-200 dark:border-secondary-700 p-4">
            <div className="flex items-center gap-3">
              <Search className="h-5 w-5 text-gray-400 dark:text-secondary-500 flex-shrink-0" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search help articles... (Type to search, ↑↓ to navigate, ↵ to select)"
                className="flex-1 outline-none text-lg placeholder:text-gray-400 dark:text-secondary-500"
                autoFocus
              />
              <kbd className="hidden sm:inline-block px-2 py-1 text-xs font-semibold text-gray-500 dark:text-secondary-400 bg-gray-100 dark:bg-secondary-700 border border-gray-300 dark:border-secondary-600 rounded">
                ESC
              </kbd>
            </div>
          </div>

          {/* Results */}
          <div className="max-h-[60vh] overflow-y-auto">
            {searchResults.length === 0 ? (
              <div className="p-8 text-center text-gray-500 dark:text-secondary-400">
                <Book className="h-12 w-12 mx-auto mb-3 text-gray-300 dark:text-secondary-600" />
                <p className="text-lg font-medium mb-1">
                  {searchQuery ? 'No results found' : 'Start typing to search'}
                </p>
                <p className="text-sm">
                  {searchQuery
                    ? 'Try different keywords or browse the help center'
                    : 'Search through all help articles and documentation'}
                </p>
              </div>
            ) : (
              <div className="py-2">
                {searchResults.map((result, index) => (
                  <CommandPaletteItem
                    key={result.article.id}
                    article={result.article}
                    isSelected={index === selectedIndex}
                    onClick={() => handleSelect(result.article)}
                    onMouseEnter={() => setSelectedIndex(index)}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-gray-200 dark:border-secondary-700 px-4 py-3 bg-gray-50 dark:bg-secondary-900 flex items-center justify-between text-xs text-gray-500 dark:text-secondary-400">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-1">
                <kbd className="px-2 py-1 font-semibold bg-white dark:bg-secondary-800 border border-gray-300 dark:border-secondary-600 rounded">
                  ↑↓
                </kbd>
                <span>Navigate</span>
              </div>
              <div className="flex items-center gap-1">
                <kbd className="px-2 py-1 font-semibold bg-white dark:bg-secondary-800 border border-gray-300 dark:border-secondary-600 rounded">
                  ↵
                </kbd>
                <span>Select</span>
              </div>
              <div className="flex items-center gap-1">
                <kbd className="px-2 py-1 font-semibold bg-white dark:bg-secondary-800 border border-gray-300 dark:border-secondary-600 rounded">
                  ESC
                </kbd>
                <span>Close</span>
              </div>
            </div>
            <div className="hidden sm:block">
              {searchResults.length > 0 && (
                <span>{searchResults.length} result{searchResults.length !== 1 ? 's' : ''}</span>
              )}
            </div>
          </div>
        </motion.div>
      </div>
    </>
  );
};

// Command Palette Item Component
interface CommandPaletteItemProps {
  article: HelpArticle;
  isSelected: boolean;
  onClick: () => void;
  onMouseEnter: () => void;
}

const CommandPaletteItem: React.FC<CommandPaletteItemProps> = ({
  article,
  isSelected,
  onClick,
  onMouseEnter,
}) => {
  // Strip markdown and get plain text for preview
  const getPreviewText = (content: string) => {
    return content
      .replace(/#{1,6}\s/g, '') // Remove heading markers
      .replace(/\*\*(.+?)\*\*/g, '$1') // Remove bold
      .replace(/\*(.+?)\*/g, '$1') // Remove italic
      .replace(/`(.+?)`/g, '$1') // Remove inline code
      .replace(/\[(.+?)\]\(.+?\)/g, '$1') // Remove links, keep text
      .replace(/^\s*[-*+]\s/gm, '') // Remove list markers
      .replace(/^\s*\d+\.\s/gm, '') // Remove numbered list markers
      .replace(/\n+/g, ' ') // Replace newlines with spaces
      .trim()
      .substring(0, 80);
  };

  return (
    <button
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      className={`w-full text-left px-4 py-3 flex items-center gap-3 transition-colors ${
        isSelected ? 'bg-blue-50 dark:bg-blue-950 border-l-2 border-blue-600' : 'border-l-2 border-transparent'
      }`}
    >
      <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
        <Book className="h-5 w-5 text-white" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <h4 className={`font-medium truncate ${isSelected ? 'text-blue-900' : 'text-gray-900 dark:text-white'}`}>
            {article.title}
          </h4>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <span
            className={`px-2 py-0.5 rounded-full ${
              isSelected ? 'bg-blue-200 text-blue-800' : 'bg-gray-200 dark:bg-secondary-700 text-gray-700 dark:text-secondary-200'
            }`}
          >
            {article.category}
          </span>
          <span className="text-gray-500 dark:text-secondary-400 truncate">
            {getPreviewText(article.content)}...
          </span>
        </div>
      </div>
      <ChevronRight
        className={`h-5 w-5 flex-shrink-0 ${isSelected ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400 dark:text-secondary-500'}`}
      />
    </button>
  );
};
